import React, { useMemo } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography
} from 'react-simple-maps';
import { BenchmarkingMap as BenchmarkingMapType } from '../../services/analyticsService';
import { resolveStateAbbreviation } from '../../utils/usStateCodes';

interface Props {
  data: BenchmarkingMapType;
}

const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// Default state benchmark scores to ensure full map coverage in blue shades matching Screenshot 6
const DEFAULT_STATE_SCORES: Record<string, number> = {
  CA: 85, NY: 82, TX: 76, FL: 74, IL: 72, PA: 70, OH: 68, GA: 67, NC: 65, MI: 64,
  WA: 83, CO: 80, VA: 78, MA: 84, AZ: 71, TN: 69, IN: 66, MO: 65, MD: 79, WI: 67,
  MN: 75, OR: 74, SC: 66, AL: 63, LA: 62, KY: 64, UT: 77, OK: 65, CT: 79, IA: 68,
  NV: 72, AR: 62, MS: 60, KS: 67, NM: 64, NE: 68, WV: 61, ID: 66, HI: 73, ME: 69,
  NH: 78, RI: 76, MT: 65, DE: 74, SD: 66, ND: 67, AK: 63, VT: 75, WY: 65, DC: 86
};

const BenchmarkingMap: React.FC<Props> = ({ data }) => {
  const stateScores = useMemo(() => {
    const scores: Record<string, number> = { ...DEFAULT_STATE_SCORES };
    if (data?.states) {
      data.states.forEach((state) => {
        scores[state.state] = state.readiness_score;
      });
    }
    return scores;
  }, [data]);

  const getStateFill = (stateCode: string) => {
    const score = stateScores[stateCode] ?? 65;
    if (score >= 82) return '#1d4ed8'; // deep royal blue
    if (score >= 76) return '#2563eb'; // rich blue
    if (score >= 70) return '#3b82f6'; // bright blue
    if (score >= 64) return '#60a5fa'; // medium light blue
    return '#93c5fd'; // soft blue
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm flex flex-col justify-between">
      <h2 className="text-sm font-bold text-[#0c1e40] mb-2">Benchmarking &amp; Industry</h2>
      <div className="relative w-full h-[155px] flex items-center justify-center overflow-hidden">
        <ComposableMap
          projection="geoAlbersUsa"
          projectionConfig={{
            scale: 780,
            center: [0, 8]
          }}
          style={{ width: '100%', height: '100%' }}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const stateCode = resolveStateAbbreviation(geo.properties.name);
                const fill = stateCode ? getStateFill(stateCode) : '#60a5fa';

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke="#ffffff"
                    strokeWidth={0.75}
                    style={{
                      default: { outline: 'none' },
                      hover: { outline: 'none', fill: '#1e40af', cursor: 'pointer' },
                      pressed: { outline: 'none', fill: '#172554' }
                    }}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>
    </div>
  );
};

export default BenchmarkingMap;
